import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import InfiniteScrollTable from 'components/ui/InfiniteScrollTable'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import { getUserRounds } from 'store/user/thunks'
import SingleRoundRow from 'components/game/SingleRoundRow'

interface Props {
  userState: UserState
}

export default function UserGames({ userState }: Props): JSX.Element {
  const intl = useIntl()
  const [currentUserId, setCurrentUserId] = useState<number | null>(null)
  const [lastFetchedCursor, setLastFetchedCursor] = useState<string | null>(null)

  const dispatch = useDispatch()

  const { user, userRounds, userRoundsStatus, roundsCursor, status } = userState

  useEffect(() => {
    if (user.id && ((user.id && userRoundsStatus === 'idle') || user.id !== currentUserId)) {
      const data = {
        id: user.id,
      }
      setCurrentUserId(user.id)
      dispatch(getUserRounds(data))
    }
  }, [dispatch, userRoundsStatus, user, currentUserId])

  const loadMoreRounds = () => {
    if (user.id && lastFetchedCursor !== roundsCursor) {
      const data = {
        id: user.id,
        params: { cursor: roundsCursor },
      }
      setLastFetchedCursor(roundsCursor)
      dispatch(getUserRounds(data))
    }
  }

  const hasMoreRounds = () => {
    return roundsCursor !== null
  }

  const columns: TableColumn[] = [
    {
      id: 'date',
      label: intl.formatMessage({ id: 'strings.date' }),
    },
    {
      id: 'name',
      label: intl.formatMessage({ id: 'strings.name' }),
    },
    {
      id: 'club',
      label: intl.formatMessage({ id: 'strings.club' }),
    },
    {
      id: 'course',
      label: intl.formatMessage({ id: 'strings.course' }),
    },
    {
      id: 'options',
      label: intl.formatMessage({ id: 'strings.options' }),
    },
    {
      id: 'creator',
      label: intl.formatMessage({ id: 'strings.createdBy' }),
    },
    {
      id: 'codes',
      label: intl.formatMessage({ id: 'strings.joinViewCode' }),
    },
  ]

  const mapRoundPlayerWithDefaultValues = (player: RoundPlayer | undefined): RoundPlayer => {
    return {
      profileId: player?.profileId ?? null,
      firstName: player?.firstName || '',
      lastName: player?.lastName || '',
      hcp: player?.hcp,
      avatarUrl: player?.avatarUrl || undefined,
    }
  }

  const mapUserToRoundPlayer = (user: User): RoundPlayer => {
    return {
      profileId: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      hcp: user.handicap,
      avatarUrl: user.avatarUrl,
    }
  }

  const getRoundCreator = (round: UserRound): RoundPlayer => {
    const creatorId = round.creatorId
    const isCurrentUser = creatorId === user.id
    const roundPlayer = round.withPlayers?.filter((p: RoundPlayer) => p.profileId && p.profileId === creatorId)[0]
    return isCurrentUser ? mapUserToRoundPlayer(user) : mapRoundPlayerWithDefaultValues(roundPlayer)
  }

  const getRoundPlayers = (round: UserRound): RoundPlayer[] => {
    const currentUser = mapUserToRoundPlayer(user)

    if (round?.withPlayers) {
      return round.withPlayers.map((p: RoundPlayer) => mapRoundPlayerWithDefaultValues(p))
    }

    return [currentUser]
  }

  const mapUserRoundToGameRound = (round: UserRound): GameSearchRound => {
    const creator: RoundPlayer = getRoundCreator(round)
    const players: RoundPlayer[] = getRoundPlayers(round)

    const roundDate = round.startTime ? round.startTime : round.date
    return {
      roundId: round.roundId,
      eventId: round.eventId,
      clubId: round.clubId,
      courseId: round.courseId,
      tournamentId: round.tournamentId,
      name: round.name,
      date: roundDate,
      viewCode: round.viewCode || '',
      gameCode: round.gameCode || '',
      isCompleted: round.gameCompleted,
      isPrivate: round.private,
      isHcpRound: round.hcpRound,
      isMultiround: round.isMultiround,
      courseName: round.courseName,
      clubName: round.clubName,
      creator,
      players,
      parent: round.parent,
      isSimulator: round.isSimulator,
    }
  }

  if ([userRoundsStatus, status].includes('loading') && userRounds.length === 0) {
    return (
      <SpinnerContainer>
        <LoadingSpinnerGreen size={72} />
      </SpinnerContainer>
    )
  }

  return (
    <>
      <SectionTitle>{intl.formatMessage({ id: 'strings.games' })}</SectionTitle>
      <InfiniteScrollTable
        columns={columns}
        rowCount={userRounds.length}
        hasMoreRows={hasMoreRounds()}
        loadMore={loadMoreRounds}
        noMoreRowsText={intl.formatMessage({ id: 'strings.allRoundsFetched' })}
      >
        {userRounds.map((round: UserRound, i: number) => (
          <SingleRoundRow key={i} round={mapUserRoundToGameRound(round)} user={user} />
        ))}
      </InfiniteScrollTable>
    </>
  )
}

const SpinnerContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 50px auto;
  justify-content: center;
`

const SectionTitle = styled.h3`
  font-size: 20px;
  line-height: 23px;
  text-transform: uppercase;
  font-weight: 600;
  color: ${(props) => props.theme.customPalette.darkGray2};
  margin: 10px 0 20px;
`
